import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Plus, Search, Sparkles, X } from 'lucide-react';
import { createAgencyCustomField } from '@/lib/api';
import { groupedAvailableFields } from '@/lib/agencyFields';

const TYPE_LABELS = {
  text: 'Text',
  number: 'Number',
  date: 'Date',
  select: 'Dropdown',
  boolean: 'Yes / No',
};

function CreateFieldDialog({ open, onOpenChange, onCreated, types, atLimit }) {
  const [label, setLabel] = useState('');
  const [type, setType] = useState('text');
  const [options, setOptions] = useState(['']);
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setLabel('');
    setType('text');
    setOptions(['']);
  };

  const submit = async () => {
    const trimmed = label.trim();
    if (!trimmed) {
      toast.error('Give the field a name.');
      return;
    }
    const cleanOptions = options.map((option) => option.trim()).filter(Boolean);
    if (type === 'select' && !cleanOptions.length) {
      toast.error('A dropdown field needs at least one option.');
      return;
    }
    try {
      setSaving(true);
      const { data } = await createAgencyCustomField({
        label: trimmed,
        type,
        options: cleanOptions,
      });
      toast.success(`"${data.label}" is now available on every agency.`);
      onCreated(data);
      reset();
      onOpenChange(false);
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.detail || 'Could not create the field.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(value) => (!saving ? onOpenChange(value) : null)}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Create a new agency field</DialogTitle>
          <DialogDescription>
            This adds a real field to every agency record — you can fill it in on the table and on
            each agency&apos;s profile.
          </DialogDescription>
        </DialogHeader>

        {atLimit ? (
          <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
            You have reached the maximum number of custom fields. Delete one before adding another.
          </p>
        ) : (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Field name</Label>
              <Input
                value={label}
                onChange={(event) => setLabel(event.target.value)}
                placeholder="Contract renewal date"
                maxLength={60}
              />
            </div>

            <div className="space-y-2">
              <Label>Type</Label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(types.length ? types : Object.keys(TYPE_LABELS)).map((value) => (
                    <SelectItem key={value} value={value}>{TYPE_LABELS[value] || value}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-[#062B1A]/50">
                The type cannot be changed later without clearing the values already stored.
              </p>
            </div>

            {type === 'select' && (
              <div className="space-y-2">
                <Label>Options</Label>
                {options.map((option, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <Input
                      value={option}
                      onChange={(event) => setOptions((current) => current.map(
                        (item, position) => (position === index ? event.target.value : item),
                      ))}
                      placeholder={`Option ${index + 1}`}
                      maxLength={60}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      disabled={options.length === 1}
                      onClick={() => setOptions((current) => current.filter((_, position) => position !== index))}
                      aria-label={`Remove option ${index + 1}`}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setOptions((current) => [...current, ''])}
                >
                  <Plus className="mr-2 h-3.5 w-3.5" /> Add option
                </Button>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={submit}
            disabled={saving || atLimit}
            className="bg-[#062B1A] text-white hover:bg-[#041B10]"
          >
            {saving ? 'Creating...' : 'Create field'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AgencyColumnPicker({ fieldIndex, usedIds, onAddColumn, onFieldCreated }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [createOpen, setCreateOpen] = useState(false);

  const groups = useMemo(() => {
    const available = groupedAvailableFields(fieldIndex, usedIds);
    const term = query.trim().toLowerCase();
    if (!term) return available;
    return available
      .map(({ group, fields }) => ({
        group,
        fields: fields.filter((field) => field.label.toLowerCase().includes(term)),
      }))
      .filter(({ fields }) => fields.length);
  }, [fieldIndex, usedIds, query]);

  const customCount = (fieldIndex?.custom || []).length;
  const atLimit = customCount >= (fieldIndex?.maxCustomFields || 40);

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button type="button" variant="outline" size="sm" className="border-[#D4AF37]/45 text-[#8A6508]">
            <Plus className="mr-2 h-4 w-4" /> Add column
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-80 p-0">
          <div className="border-b border-[rgba(6,43,26,0.10)] p-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-[#062B1A]/35" />
              <Input
                autoFocus
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search fields..."
                className="h-9 pl-8"
              />
            </div>
          </div>

          <div className="max-h-72 overflow-y-auto p-1">
            {groups.length ? groups.map(({ group, fields }) => (
              <div key={group} className="mb-1">
                <p className="px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-[#062B1A]/45">
                  {group}
                </p>
                {fields.map((field) => (
                  <button
                    key={field.id}
                    type="button"
                    onClick={() => {
                      onAddColumn(field);
                      setOpen(false);
                      setQuery('');
                    }}
                    className="flex w-full items-center justify-between gap-2 rounded px-2 py-1.5 text-left text-sm text-[#062B1A] hover:bg-[#F1F3F5]"
                  >
                    <span className="truncate">{field.label}</span>
                    <span className="shrink-0 text-[10px] uppercase text-[#062B1A]/35">
                      {TYPE_LABELS[field.type] || field.type}
                    </span>
                  </button>
                ))}
              </div>
            )) : (
              <p className="px-3 py-6 text-center text-sm text-[#062B1A]/50">
                {query ? 'No matching fields.' : 'Every field is already on the table.'}
              </p>
            )}
          </div>

          <div className="border-t border-[rgba(6,43,26,0.10)] p-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="w-full justify-start text-[#8A6508]"
              onClick={() => {
                setOpen(false);
                setCreateOpen(true);
              }}
            >
              <Sparkles className="mr-2 h-4 w-4" /> Create a new field
            </Button>
          </div>
        </PopoverContent>
      </Popover>

      <CreateFieldDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        types={fieldIndex?.types || []}
        atLimit={atLimit}
        onCreated={(field) => {
          onFieldCreated(field);
          onAddColumn(field);
        }}
      />
    </>
  );
}
